import React from 'react'

import { FormItem } from '@react/react-spectrum/Form'
import Heading from '@react/react-spectrum/Heading'

import ColorInput from 'components/ColorInput'
import MediaUpload from 'components/MediaUpload'
import PlainTextEditor from 'components/PlainTextEditor'
import { FormTableInput } from 'components/FormTable'
import Select from '@react/react-spectrum/Select'
import { Image } from '@react/react-spectrum/Image'

import './CustomerHubBrandingForm.scss'

const ADOBE_LOGO_COLOR_OPTIONS = [
  {
    label: 'Icon',
    value: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_logo_red_vertical.svg',
  },
  {
    label: 'Wordmark',
    value:
      'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_logo_red_horizontal.svg',
  },
  {
    label: 'Icon (alternative)',
    value: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_icon_red.svg',
  },
  {
    label: 'Wordmark (alternative)',
    value: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_wordmark_red.svg',
  },
]

const ADOBE_LOGO_MONOCHROME_OPTIONS = [
  {
    label: 'Icon',
    value:
      'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_logo_white_vertical.svg',
  },
  {
    label: 'Wordmark',
    value:
      'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_logo_white_horizontal.svg',
  },
  {
    label: 'Icon (alternative)',
    value: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_icon_white.svg',
  },
  {
    label: 'Wordmark (alternative)',
    value: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/luma/customer-hub/Adobe_wordmark_white.svg',
  },
]

const ANIMATION_SELECTORS_FIELDS_DEFAULTS = [
  { selector: '.CustomerHubLogo .logos' },
  { selector: '.CustomerHubLogo .Button' },
  { selector: '.CustomerHubVideo .foregroundVideo', delay: 200 },
  { selector: '.CustomerHubVideo .logo + .Tagline', delay: 200, composeTextSelector: true },
  { selector: '.CustomerHubVideo .Video + .Tagline', delay: 400, composeTextSelector: true },
  { selector: '.PartnershipCard .Title__content', composeTextSelector: true, skipAutoDelay: true },
  { selector: '.PartnershipCard .Button' },
  { selector: '.PartnershipCard .Card__image', delay: 400 },
  {
    selector: '.Container__content > section > .Title > .Title__content',
    composeTextSelector: true,
    skipAutoDelay: true,
  },
  {
    selector: '.Container__content > section > .Text > .Text__content',
    composeTextSelector: true,
    skipAutoDelay: true,
  },
  { selector: '.CustomerHubSharedVision .Card', delay: 200 },
  { selector: '.StrategicPillars .PillarsHeader' },
  { selector: '.StrategicPillars .CustomerHubStrategicPillar', delay: 200 },
  { selector: '.CustomerHubVisionDemo .Video' },
  { selector: '.Tiles .Card' },
  { selector: '.SuccessStories .CustomerHubSuccessStory' },
  { selector: '.Deliverables .Card' },
  { selector: '.AccountTeam .Card' },
  { selector: '.Container__content > section > .Card' },
  { selector: '.CustomerHubAdobeSuccessPlan', delay: 200 },
]

const ANIMATION_SELECTORS_COLUMNS = [
  {
    name: 'selector',
    label: 'CSS Selector',
    type: 'string',
    placeholder: 'e.g. .Container .Card',
    tooltip: 'Valid CSS Selector for element to be animated',
  },
  {
    name: 'delay',
    label: 'Delay',
    type: 'integer',
    defaultValue: 0,
    tooltip: 'Animation delay in milliseconds',
  },
  {
    name: 'composeTextSelector',
    label: 'Include text elements',
    type: 'boolean',
    defaultValue: 'false',
    tooltip: 'Animate text elements inside selected container instead of the container itself',
  },
  {
    name: 'skipAutoDelay',
    label: 'Skip auto-delay',
    type: 'boolean',
    defaultValue: 'false',
    tooltip: "Skip animation's auto delay for same-kind elements",
  },
]

const THEME_OPTIONS = [{ label: 'Customer Hub', value: 'customer-hub' }]
const PROVIDER_THEME_OPTIONS = [
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
]

export default function CustomerHubBrandingForm({ branding, project, disabled, onChange }) {
  const customStylesheet = branding.customStylesheet
    ?.replaceAll(/(\{\s*)/g, '{\n  ')
    .replaceAll(/( *);( *)/g, ';\n  ')
    .replaceAll(/(\s*)\}(\s*)/g, '\n}\n')
    .replaceAll(/ {2,}/g, '  ')
  const updateStylesheet = (v) => onChange('customStylesheet')(v.replaceAll('\n', '').replaceAll(/\s{2,}/g, ' '))

  const customScript = branding.customScript
    ?.replaceAll(/(\{\s*)/g, '{\n  ')
    .replaceAll(/( *);( *)/g, ';\n  ')
    .replaceAll(/(\s*)\}(\s*)/g, '\n}\n')
    .replaceAll(/ {2,}/g, '  ')
  const updateScript = (v) => onChange('customScript')(v.replaceAll('\n', '').replaceAll(/\s{2,}/g, ' '))

  return (
    <>
      <section>
        <Heading size='4'>Customer Logo (color)</Heading>
        <MediaUpload
          type='image'
          value={branding.customerLogoColor}
          heading='Drag and Drop'
          onUpload={onChange('customerLogoColor')}
          projectId={project?.meta?.id}
          path={'branding-customerLogoColor'}
          withTextfield={true}
          disabled={disabled}
        />

        <Heading size='4'>Customer Logo (monochrome)</Heading>
        <MediaUpload
          type='image'
          value={branding.customerLogoMonochrome}
          heading='Drag and Drop'
          onUpload={onChange('customerLogoMonochrome')}
          projectId={project?.meta?.id}
          path={'branding-customerLogoMonochrome'}
          withTextfield={true}
          disabled={disabled}
        />

        <Heading size='4'>Adobe Logo (color)</Heading>
        <Select
          value={branding.adobeLogoColor || ''}
          options={ADOBE_LOGO_COLOR_OPTIONS}
          disabled={disabled}
          onChange={onChange('adobeLogoColor')}
        />
        <div className='image-container'>
          <Image src={branding.adobeLogoColor} alt='uploaded-image' />
        </div>

        <Heading size='4'>Adobe Logo (monochrome)</Heading>
        <Select
          value={branding.adobeLogoMonochrome || ''}
          options={ADOBE_LOGO_MONOCHROME_OPTIONS}
          disabled={disabled}
          onChange={onChange('adobeLogoMonochrome')}
        />
        <div className='image-container'>
          <Image src={branding.adobeLogoMonochrome} alt='uploaded-image' />
        </div>
      </section>

      <section>
        <Heading size='4'>Colors</Heading>

        <FormItem label='Primary'>
          <ColorInput
            name='primaryColor'
            value={branding.primaryColor}
            onChange={onChange('primaryColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Secondary'>
          <ColorInput
            name='secondaryColor'
            value={branding.secondaryColor}
            onChange={onChange('secondaryColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Body Background'>
          <ColorInput
            name='backgroundColor'
            value={branding.backgroundColor}
            onChange={onChange('backgroundColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Body Secondary Background'>
          <ColorInput
            name='secondaryBackgroundColor'
            value={branding.secondaryBackgroundColor}
            onChange={onChange('secondaryBackgroundColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Body Text'>
          <ColorInput
            name='primaryTextColor'
            value={branding.primaryTextColor}
            onChange={onChange('primaryTextColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Inverted Text'>
          <ColorInput
            name='invertedTextColor'
            value={branding.invertedTextColor}
            onChange={onChange('invertedTextColor')}
            disabled={disabled}
          />
        </FormItem>

        <FormItem label='Link'>
          <ColorInput
            name='linkColor'
            value={branding.linkColor}
            onChange={onChange('linkColor')}
            disabled={disabled}
          />
        </FormItem>
      </section>

      <section>
        <Heading size='4'>Additional Styles</Heading>

        <FormItem label='Theme'>
          <Select
            value={branding.theme || ''}
            options={THEME_OPTIONS}
            disabled={disabled}
            onChange={onChange('theme')}
          />
        </FormItem>

        <FormItem label='Provider Theme'>
          <Select
            value={branding.providerTheme || ''}
            options={PROVIDER_THEME_OPTIONS}
            disabled={disabled}
            onChange={onChange('providerTheme')}
          />
        </FormItem>

        <PlainTextEditor
          buttonLabel='Custom CSS'
          name='customStylesheet'
          defaultValue={customStylesheet}
          disabled={disabled}
          onChange={updateStylesheet}
        />

        <Heading size='4' style={{ marginTop: '24px' }}>
          Custom script
        </Heading>

        <PlainTextEditor
          buttonLabel='Custom JS'
          name='customScript'
          defaultValue={customScript}
          disabled={disabled}
          onChange={updateScript}
        />

        <Heading size='4' style={{ marginTop: '24px' }}>
          Animations
        </Heading>

        <p>Configure animated elements on page scroll.</p>

        <FormTableInput
          name='animationSelectors'
          label='Animated elements selectors'
          columns={ANIMATION_SELECTORS_COLUMNS}
          fields={branding.animationSelectors ?? ANIMATION_SELECTORS_FIELDS_DEFAULTS}
          disabled={disabled}
          saveAs='array'
          onChange={onChange('animationSelectors')}
        />
      </section>
    </>
  )
}
